import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useRecoilValue } from 'recoil';
import { TextField, MenuItem, CircularProgress, IconButton } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { tr } from '@/common/utils/tr';
import trI18n from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import useDebounce from '@/hooks/useDebounce';
import Utils from '@/common/utils/utils';
import localStorageUtil from '@/common/utils/localStorageUtil';
import Constant from '@/common/constant/Constant';
import TranslationConstant from '@/common/constant/TranslationConstant';
import { userAtom } from '@/state/atom/userAtom';
import translationService from '@/service/translationService';
import TranslationManagementConstant from './TranslationManagementConstant';
import TranslationRowDetail from './components/TranslationRowDetail';
import TerminologyDraftCard from './components/TerminologyDraftCard';
import TerminologyKeywordCell from './components/TerminologyKeywordCell';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import MuiTooltip from '@/component/common/MuiTooltip';
import './TranslationManagement.css';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';
import { isOverTranslationMaxLength } from '@/common/utils/translationUtils';

const PAGE_SIZE = TranslationManagementConstant.PAGE_SIZE;

// How many rows' details load in parallel when a search opens every result row.
const EXPAND_CONCURRENCY = 5;

const { NEW_ROW_ERROR } = TranslationManagementConstant;

// Server error code of a Terminology write -> NEW_ROW_ERROR shown on the Draft Card.
const TERMINOLOGY_ERROR_MAP = {
    [Constant.TRANSLATION_API_ERROR_CODE.TERMINOLOGY_KEYWORD_REQUIRED]: NEW_ROW_ERROR.REQUIRED,
    [Constant.TRANSLATION_API_ERROR_CODE.TERMINOLOGY_KEYWORD_DUPLICATE]: NEW_ROW_ERROR.DUPLICATE,
    [Constant.TRANSLATION_API_ERROR_CODE.TERMINOLOGY_TEXT_TOO_LONG]: NEW_ROW_ERROR.MAX_LENGTH,
    [Constant.TRANSLATION_API_ERROR_CODE.TERMINOLOGY_TRANSLATION_REQUIRED]: NEW_ROW_ERROR.PIVOT_REQUIRED
};

/** A Terminology entry: keyed on its entryId, written through the *_TERMINOLOGY actions. */
const isTerminologyEntry = item => item?.usage === Constant.TRANSLATION_USAGE.TERMINOLOGY;

/** NEW_ROW_ERROR code -> translated message. Called while rendering. */
const renderNewRowError = errorCode => {
    switch (errorCode) {
        case NEW_ROW_ERROR.REQUIRED:
            return trI18n('MSG-CM-REQUIRED-INPUT');
        case NEW_ROW_ERROR.MAX_LENGTH:
            return trI18n('COMMSG027').replace('{{maxLength}}', Constant.TRANSLATION_KEYWORD_MAX_LENGTH);
        case NEW_ROW_ERROR.DUPLICATE:
            return tr('このキーワードは既に登録されています。');
        // TODO: replace with the message key MSG-MLT-TRANSLATION-REQUIRED once BA confirms the text.
        case NEW_ROW_ERROR.PIVOT_REQUIRED:
            return tr('日本語の翻訳を入力してください。');
        case NEW_ROW_ERROR.TRANSLATION_REQUIRED:
            return tr('翻訳を1つ以上入力してください。');
        default:
            return '';
    }
};

// One grid row = one (usage, sourceHash): the same 原文 can exist under two usages.
const rowKeyOf = item => `${item.usage}#${item.sourceHash}`;

// Search box debounce (BR: 500ms after the admin stops typing, no 検索 button).
const SEARCH_DEBOUNCE_MS = 500;

// Same dropdown behaviour as the other screens (see e.g. NoticeTicket): the menu
// opens directly under the input instead of over it, and `selectDrop` (App.css)
// applies the shared menu style + max-height so a long list (e.g. languages) scrolls.
const SELECT_PROPS = {
    // Show the selected option even when its value is '' (the "すべて" option).
    displayEmpty: true,
    MenuProps: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
        getContentAnchorEl: null,
        // `tm-select-menu` caps the menu height to the viewport (TranslationManagement.css).
        className: 'selectDrop tm-select-menu'
    }
};

/**
 * Header of the toggle column. The label is translated as a whole (never split into
 * fragments); only the untranslated source text gets the design's fixed line break.
 */
const renderTermHeader = () => {
    const { TEXT, LINE_BREAK_AT } = TranslationManagementConstant.TERM_HEADER;
    const label = tr(TEXT);
    if (label !== TEXT) return label;
    return (
        <>
            {TEXT.slice(0, LINE_BREAK_AT)}
            <br />
            {TEXT.slice(LINE_BREAK_AT)}
        </>
    );
};

/**
 * System-styled ON/OFF toggle (matches the .switch-check pattern used on the
 * settings screens): a hidden checkbox + a label rendered as a slider via CSS.
 */
function TmSwitch({ id, checked, disabled, onChange }) {
    return (
        <span className="tm-switch">
            <input
                id={id}
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={onChange}
            />
            <label htmlFor={id} />
        </span>
    );
}

TmSwitch.propTypes = {
    id: PropTypes.string.isRequired,
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    onChange: PropTypes.func
};

TmSwitch.defaultProps = {
    checked: false,
    disabled: false
};

/**
 * 翻訳管理 (Translation Management) screen.
 *
 * Grid lists one row per 原文 (SOURCE docs from OpenSearch). Opening a row lazily
 * loads its per-language translations (ROW docs). Every write goes through
 * translateUpdateApi; the org is re-derived server-side from the caller.
 *
 * Filtering is fully server-side: keyword (debounced), 種別 (usage), 翻訳元言語
 * (sourceLanguage), 翻訳先言語 (targetLanguage) and 状態 (status) are all pushed to
 * translateUpdateApi. 用語 (terminology) is the only client-side narrowing, since
 * that flag lives on the SOURCE doc already loaded on the page.
 */
export default function TranslationManagement() {
    // Re-render when the translation dictionary loads/extends so tr() labels update.
    useTranslationSubscription();

    const { enqueueSnackbar } = useSnackbar();
    const userDetail = useRecoilValue(userAtom);

    // The user's current display language (target of a hand-added translation).
    const currentLanguage =
        Utils.convertJSONDataToObject(userDetail?.userSetting)?.mainLanguage ||
        TranslationConstant.DEFAULT_MAIN_LANGUAGE;

    // toolbar state
    const [keyword, setKeyword] = useState('');
    const debouncedKeyword = useDebounce(keyword, SEARCH_DEBOUNCE_MS);
    const [usageFilter, setUsageFilter] = useState('');
    const [sourceLanguageFilter, setSourceLanguageFilter] = useState('');
    const [targetLanguageFilter, setTargetLanguageFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [termFilter, setTermFilter] = useState(TranslationManagementConstant.TERM_FILTER.ALL);

    // grid state
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    // Synchronous guards for load-more: the native scroll listener can fire again
    // before React re-renders with the new isLoadingMore / items.
    const loadingMoreRef = useRef(false);
    const itemsRef = useRef([]);
    itemsRef.current = items;
    // Rows deleted locally since the list was loaded. Each one shifts the server
    // list up by one, so the next page starts that many rows earlier.
    const removedCountRef = useRef(0);

    // expand state - any number of rows may be open at once.
    // { [rowKey]: { rows: [...], loading: bool } }; a key present = that row is open.
    const [expanded, setExpanded] = useState({});
    // Bumped on every new (non-append) search so a late detail response of the
    // previous list cannot write into the new one.
    const searchGenerationRef = useRef(0);
    // Id of the latest new (non-append) search request. Typing fires several
    // debounced searches (e.g. 'g' then 'gà' via IME); a slower earlier one must
    // not overwrite the result of the latest one.
    const latestSearchIdRef = useRef(0);

    // Terminology Draft Card state (create a whole entry in one step).
    const [addingNew, setAddingNew] = useState(false);
    // Error code returned by the server for the open Draft Card (e.g. duplicate).
    const [newRowError, setNewRowError] = useState('');
    // Languages the org translates into: the translation rows of the Draft Card.
    const [orgLanguages, setOrgLanguages] = useState([]);
    // Grid row (any usage) waiting for the delete confirmation, or null.
    const [deletingItem, setDeletingItem] = useState(null);

    // inline edit state - dòng con (翻訳). Click a translated text to edit; blur saves.
    // { rowKey, targetLanguage } of the cell being edited, or null.
    const [editing, setEditing] = useState(null);
    const [editText, setEditText] = useState('');
    // NEW_ROW_ERROR code shown under the input being edited ('' = none).
    const [editError, setEditError] = useState('');

    const [submitting, setSubmitting] = useState(false);

    const getAuth = () => ({
        affiliationOrganization: localStorageUtil.getAffiliationOrganization(),
        userId: localStorageUtil.getUserDetailId()
    });

    const showSuccess = () =>
        enqueueSnackbar(buildSnackbarMessage('MSG-CM-BULK-ACTION-PROCESS-SUCCESSFULLY'), {
            variant: 'success',
            autoHideDuration: 3000
        });
    const showError = () =>
        enqueueSnackbar(buildSnackbarMessage('SYSMSG001'), { variant: 'error', autoHideDuration: 4000 });

    /**
     * Runs the grid search for a given page. All filters (keyword, usage,
     * source/target language, status, 用語) are pushed to the backend. Appends
     * when loading more.
     */
    const runSearch = useCallback(
        async (nextPage = 0, append = false) => {
            const { affiliationOrganization, userId } = getAuth();
            // Generation of the list this request belongs to (see the append guard below).
            const generation = searchGenerationRef.current;
            // Only a new search takes an id; load-more is guarded by generation.
            const searchId = append ? null : ++latestSearchIdRef.current;
            const isStaleSearch = () => !append && searchId !== latestSearchIdRef.current;
            // Auto-Discard: a search, a filter change or load-more drops an unsaved Draft Card.
            setAddingNew(false);
            setNewRowError('');
            if (append) {
                loadingMoreRef.current = true;
                setIsLoadingMore(true);
            } else {
                setIsLoading(true);
            }
            try {
                const result = await translationService.searchTranslationSource({
                    affiliationOrganization,
                    userId,
                    keyword: debouncedKeyword.trim(),
                    usage: usageFilter,
                    sourceLanguage: sourceLanguageFilter,
                    targetLanguage: targetLanguageFilter,
                    status: statusFilter,
                    useAsTerminology: termFilter,
                    from: Math.max(nextPage * PAGE_SIZE - (append ? removedCountRef.current : 0), 0),
                    size: PAGE_SIZE,
                    // BR-35: newest record first.
                    sortField: TranslationManagementConstant.SORT_FIELD.CREATED_AT,
                    sortOrder: TranslationManagementConstant.SORT_ORDER.DESC,
                    withOrgLanguages: !append
                });
                // A load-more of a list that was replaced meanwhile (new search / filter)
                // must not be appended to the new list.
                if (append && generation !== searchGenerationRef.current) {
                    return;
                }
                // A newer search started meanwhile: this response is outdated.
                if (isStaleSearch()) {
                    return;
                }
                if (Array.isArray(result.orgLanguages)) {
                    setOrgLanguages(result.orgLanguages);
                }
                let newItems = result.items || [];
                if (!append) {
                    // A new list: collapse every open row, drop late responses of the old one.
                    searchGenerationRef.current += 1;
                    removedCountRef.current = 0;
                    setEditing(null);
                    setExpanded({});
                    setItems(newItems);
                } else {
                    // Drop rows already on screen (e.g. a 用語 added locally that the index
                    // now returns too), so a row is never listed twice.
                    const shownKeys = new Set(itemsRef.current.map(rowKeyOf));
                    newItems = newItems.filter(item => !shownKeys.has(rowKeyOf(item)));
                    setItems(prev => {
                        const prevKeys = new Set(prev.map(rowKeyOf));
                        return [...prev, ...newItems.filter(item => !prevKeys.has(rowKeyOf(item)))];
                    });
                }
                setTotal(result.total || 0);
                setPage(nextPage);
                // While searching, every result row opens by default so the matching
                // translations are visible (BR-31, BR-34). Load-more rows open too.
                if (debouncedKeyword.trim()) {
                    expandAll(newItems);
                }
            } finally {
                if (append) {
                    loadingMoreRef.current = false;
                    setIsLoadingMore(false);
                } else if (!isStaleSearch()) {
                    // The latest search owns the spinner; an outdated one must not hide it.
                    setIsLoading(false);
                }
            }
        },
        // expandAll only touches state setters and refs, so it is safe to omit.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [debouncedKeyword, usageFilter, sourceLanguageFilter, targetLanguageFilter, statusFilter, termFilter]
    );

    // Re-run from page 0 whenever any filter (or the debounced keyword) changes.
    // Every filter, 用語 included, is applied server-side so the list and the hit
    // count always come from the same query. runSearch is memoised on those inputs.
    useEffect(() => {
        runSearch(0);
    }, [runSearch]);

    /** Fetches the language rows of one 原文 into its expanded entry (if still open). */
    const loadDetail = async item => {
        const key = rowKeyOf(item);
        const generation = searchGenerationRef.current;
        const { affiliationOrganization, userId } = getAuth();

        setExpanded(prev => ({ ...prev, [key]: { rows: prev[key]?.rows || [], loading: true } }));
        let rows = [];
        try {
            const result = await translationService.expandTranslationRow({
                affiliationOrganization,
                userId,
                sourceHash: item.sourceHash,
                usage: item.usage,
                sourceLanguage: item.sourceLanguage
            });
            rows = result.items || [];
        } finally {
            // Ignore the response when the list was replaced or the row was closed meanwhile.
            if (generation === searchGenerationRef.current) {
                setExpanded(prev => (prev[key] ? { ...prev, [key]: { rows, loading: false } } : prev));
            }
        }
    };

    /** Opens several rows, loading their details a few at a time. */
    const expandAll = async rowItems => {
        const generation = searchGenerationRef.current;
        for (let start = 0; start < rowItems.length; start += EXPAND_CONCURRENCY) {
            if (generation !== searchGenerationRef.current) return;
            await Promise.all(rowItems.slice(start, start + EXPAND_CONCURRENCY).map(loadDetail));
        }
    };

    /** Opens / closes one row; other open rows stay as they are. */
    const handleToggleRow = item => {
        const key = rowKeyOf(item);
        if (expanded[key]) {
            // Closing the row being edited must not leave a dangling editor.
            if (editing?.rowKey === key) handleCancelEditRow();
            setExpanded(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
            return;
        }
        loadDetail(item);
    };

    /** Toggle 用語 on every language row of the 原文 (Q1=A). */
    const handleToggleTerminology = async (item, checked) => {
        const { affiliationOrganization, userId } = getAuth();
        // Every language of this 原文. When the row is open we have the exact list;
        // otherwise fetch it first so no language is missed.
        let rows = expanded[rowKeyOf(item)]?.rows || [];
        if (rows.length === 0) {
            const result = await translationService.expandTranslationRow({
                affiliationOrganization,
                userId,
                sourceHash: item.sourceHash,
                usage: item.usage,
                sourceLanguage: item.sourceLanguage
            });
            rows = result.items || [];
        }
        // Only persisted rows: an untranslated placeholder (no id, no text) has no DB row
        // to flag. A placeholder translated in this session has text but no id yet.
        const targetLanguages = [
            ...new Set(
                rows
                    .filter(r => r.id || r.translatedText)
                    .map(r => r.targetLanguage)
                    .filter(Boolean)
            )
        ];
        if (targetLanguages.length === 0) {
            showError();
            return;
        }

        setSubmitting(true);
        try {
            const res = await translationService.toggleTerminology({
                affiliationOrganization,
                userId,
                usage: item.usage,
                sourceText: item.sourceText,
                sourceHash: item.sourceHash,
                useAsTerminology: checked,
                targetLanguages
            });
            if (res) {
                showSuccess();
                // Reflect the new flag locally without a full reload.
                setItems(prev =>
                    prev.map(it =>
                        it.sourceHash === item.sourceHash && it.usage === item.usage
                            ? { ...it, useAsTerminology: checked ? 'Y' : '' }
                            : it
                    )
                );
            } else {
                showError();
            }
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Blur-save of the keyword of a Terminology entry (parent row). The server checks
     * duplicates again and rewrites every row of the entry; the entryId never changes.
     * @returns {Promise<string>} '' on success, a NEW_ROW_ERROR code to show under the input.
     */
    const handleSaveKeyword = async (item, sourceText) => {
        const { affiliationOrganization, userId } = getAuth();
        setSubmitting(true);
        try {
            const { data, errorCode } = await translationService.updateTerminologyEntry({
                affiliationOrganization,
                userId,
                sourceHash: item.sourceHash,
                sourceText
            });
            if (data && !errorCode) {
                showSuccess();
                const key = rowKeyOf(item);
                setItems(prev => prev.map(it => (rowKeyOf(it) === key ? { ...it, sourceText } : it)));
                return '';
            }
            if (TERMINOLOGY_ERROR_MAP[errorCode]) return TERMINOLOGY_ERROR_MAP[errorCode];
            showError();
            return '';
        } finally {
            setSubmitting(false);
        }
    };

    /** Click a translated detail row -> inline edit, saved on blur via CORRECT_TRANSLATION. */
    const handleStartEditRow = (item, row) => {
        setEditing({ rowKey: rowKeyOf(item), targetLanguage: row.targetLanguage });
        setEditText(row.translatedText || '');
        setEditError('');
    };

    const handleCancelEditRow = () => {
        setEditing(null);
        setEditText('');
        setEditError('');
    };

    const handleEditTextChange = value => {
        setEditText(value);
        setEditError('');
    };

    /**
     * Blur-save of an inline edit. No-op when the text is empty or unchanged, so
     * an accidental blur cannot wipe a translation or fire a needless request.
     * A text over the length limit stays in edit mode with the error under it.
     */
    const handleSubmitEditRow = async (item, row) => {
        const value = editText.trim();
        if (!value || value === (row.translatedText || '')) {
            handleCancelEditRow();
            return;
        }
        if (isOverTranslationMaxLength(value)) {
            setEditError(NEW_ROW_ERROR.MAX_LENGTH);
            return;
        }
        const { affiliationOrganization, userId } = getAuth();
        setSubmitting(true);
        try {
            if (isTerminologyEntry(item)) {
                if (await saveEntryTranslation(item, row.targetLanguage, value)) handleCancelEditRow();
                return;
            }
            const { data, errorCode } = await translationService.correctTranslation({
                affiliationOrganization,
                userId,
                usage: item?.usage,
                targetLanguage: row.targetLanguage,
                sourceText: item?.sourceText,
                translatedText: value
            });
            if (data?.corrected) {
                showSuccess();
                handleCancelEditRow();
                // CORRECT_TRANSLATION writes DynamoDB; OpenSearch (what the detail is read
                // from) is only updated later by the stream indexer, so re-fetching now
                // would return and show the OLD text. Patch the row to the saved state.
                patchDetailRow(item, row.targetLanguage, value, TranslationManagementConstant.STATUS.CORRECTED);
            } else if (errorCode === Constant.TRANSLATION_API_ERROR_CODE.TEXT_TOO_LONG) {
                // Same place as the client-side length check: under the edited input.
                setEditError(NEW_ROW_ERROR.MAX_LENGTH);
            } else {
                showError();
            }
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Saves one translation of a Terminology entry through UPDATE_TERMINOLOGY (rows of
     * an entry are keyed on its entryId, so the text-keyed CORRECT_TRANSLATION / TRANSLATE_LABEL
     * cannot be used).
     */
    const saveEntryTranslation = async (item, targetLanguage, translatedText) => {
        const { affiliationOrganization, userId } = getAuth();
        const { data, errorCode } = await translationService.updateTerminologyEntry({
            affiliationOrganization,
            userId,
            sourceHash: item.sourceHash,
            translations: [{ targetLanguage, translatedText }]
        });
        if (errorCode || !data) {
            // A length error belongs under the edited input, like the client-side check.
            if (TERMINOLOGY_ERROR_MAP[errorCode] === NEW_ROW_ERROR.MAX_LENGTH) {
                setEditError(NEW_ROW_ERROR.MAX_LENGTH);
                return false;
            }
            showError();
            return false;
        }
        showSuccess();
        patchDetailRow(item, targetLanguage, translatedText, TranslationManagementConstant.STATUS.MANUAL);
        return true;
    };

    /** 翻訳 -> translate one still-untranslated language via TRANSLATE_LABEL/AUTO. */
    const handleTranslateRow = async (item, row) => {
        const { affiliationOrganization, userId } = getAuth();
        setSubmitting(true);
        try {
            if (isTerminologyEntry(item)) {
                // Translated from the keyword in its own language, saved as 手動 (spec §6.6 / BR-20).
                const suggestion = await translationService.previewTranslate({
                    affiliationOrganization,
                    userId,
                    text: item.sourceText,
                    sourceLanguage: item.sourceLanguage,
                    targetLanguage: row.targetLanguage
                });
                if (suggestion) {
                    await saveEntryTranslation(item, row.targetLanguage, suggestion);
                } else {
                    showError();
                }
                return;
            }
            if (row.translatedText) {
                await retranslateRow(item, row);
                return;
            }
            const map = await translationService.translateMissingTexts(
                affiliationOrganization,
                item?.usage,
                row.targetLanguage,
                [item?.sourceText],
                userId
            );
            if (map && map[item?.sourceText]) {
                showSuccess();
                // Same indexing lag as the inline edit: show the new translation directly.
                // 用語 stays 手動 even when machine-translated (spec §6.6 / BR-20).
                const status =
                    item?.usage === Constant.TRANSLATION_USAGE.TERMINOLOGY
                        ? TranslationManagementConstant.STATUS.MANUAL
                        : TranslationManagementConstant.STATUS.AUTO;
                patchDetailRow(item, row.targetLanguage, map[item.sourceText], status);
            } else {
                showError();
            }
        } finally {
            setSubmitting(false);
        }
    };

    /**
     * Translate icon on an already translated UI / FIELD_LABEL / TAG row: machine-
     * translates the 原文 again and overwrites the row as 自動 (§8.4). TRANSLATE_LABEL
     * cannot be used here - it only fills languages that have no translation yet.
     */
    const retranslateRow = async (item, row) => {
        const { affiliationOrganization, userId } = getAuth();
        const suggestion = await translationService.previewTranslate({
            affiliationOrganization,
            userId,
            text: item.sourceText,
            sourceLanguage: item.sourceLanguage,
            targetLanguage: row.targetLanguage
        });
        if (!suggestion) {
            showError();
            return;
        }
        const { data } = await translationService.correctTranslation({
            affiliationOrganization,
            userId,
            usage: item.usage,
            targetLanguage: row.targetLanguage,
            sourceText: item.sourceText,
            translatedText: suggestion,
            isAutoTranslated: true
        });
        if (!data?.corrected) {
            showError();
            return;
        }
        showSuccess();
        // Same indexing lag as the inline edit: show the new translation directly.
        patchDetailRow(item, row.targetLanguage, suggestion, TranslationManagementConstant.STATUS.AUTO);
    };

    /** Updates one language row of an open 原文 in place (no re-fetch, see callers). */
    const patchDetailRow = (item, targetLanguage, translatedText, status) => {
        const key = rowKeyOf(item);
        setExpanded(prev => {
            if (!prev[key]) return prev;
            const rows = prev[key].rows.map(detailRow =>
                detailRow.targetLanguage === targetLanguage ? { ...detailRow, translatedText, status } : detailRow
            );
            return { ...prev, [key]: { ...prev[key], rows } };
        });
    };

    /** [+] -> pin a Terminology Draft Card at the top of the list (§8.3). Usable for any org,
     * even a Japanese-only one: the card can add any target language (CR-303142). */
    const handleStartAddNew = () => {
        setAddingNew(true);
        setNewRowError('');
    };

    const handleCancelAddNew = () => {
        setAddingNew(false);
        setNewRowError('');
    };

    /**
     * [✓] / Enter on the Draft Card: saves the whole entry (keyword + translations)
     * in one step. Duplicates are checked by the server against the whole org.
     */
    const handleSaveNewRow = async draft => {
        const { affiliationOrganization, userId } = getAuth();
        setNewRowError('');
        setSubmitting(true);
        try {
            const { data, errorCode } = await translationService.createTerminologyEntry({
                affiliationOrganization,
                userId,
                ...draft
            });
            if (data && !errorCode) {
                showSuccess();
                handleCancelAddNew();
                // The index catches up later (stream indexer): show the new entry right away.
                setItems(prev => [data, ...prev]);
                setTotal(prev => prev + 1);
                return;
            }
            if (TERMINOLOGY_ERROR_MAP[errorCode]) {
                setNewRowError(TERMINOLOGY_ERROR_MAP[errorCode]);
            } else {
                showError();
            }
        } finally {
            setSubmitting(false);
        }
    };

    /** Machine translation suggestion for the Draft Card (nothing is saved). */
    const handlePreviewTranslate = async (text, sourceLanguage, targetLanguage) => {
        const { affiliationOrganization, userId } = getAuth();
        const suggestion = await translationService.previewTranslate({
            affiliationOrganization,
            userId,
            text,
            sourceLanguage,
            targetLanguage
        });
        if (!suggestion) showError();
        return suggestion;
    };

    /** [🗑] on a grid row of any usage, after the confirmation. */
    const handleConfirmDelete = async () => {
        const item = deletingItem;
        setDeletingItem(null);
        if (!item) return;

        const { affiliationOrganization, userId } = getAuth();
        setSubmitting(true);
        try {
            const { errorCode } = await translationService.deleteTranslationEntry({
                affiliationOrganization,
                userId,
                usage: item.usage,
                sourceHash: item.sourceHash
            });
            // ENTRY_NOT_FOUND = already deleted (e.g. by another admin, or a previous
            // delete whose index update failed - the server then cleans the stale
            // grid doc): the row is stale, so it leaves the grid like a successful delete.
            const isAlreadyDeleted = errorCode === Constant.TRANSLATION_API_ERROR_CODE.ENTRY_NOT_FOUND;
            if (errorCode && !isAlreadyDeleted) {
                showError();
                return;
            }
            if (!isAlreadyDeleted) showSuccess();
            await translationService.evictLocalTranslation({
                affiliationOrganization,
                usage: item.usage,
                sourceText: item.sourceText
            });
            const key = rowKeyOf(item);
            // The server list shifted up by one row: the next load-more offset follows it.
            removedCountRef.current += 1;
            setItems(prev => prev.filter(it => rowKeyOf(it) !== key));
            setTotal(prev => Math.max(prev - 1, 0));
            setExpanded(prev => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Server rows already read: every loaded page minus the rows deleted since.
    const hasMore = (page + 1) * PAGE_SIZE - removedCountRef.current < total;

    const handleLoadMore = useCallback(() => {
        if (isLoading || isLoadingMore || loadingMoreRef.current || !hasMore) return;
        runSearch(page + 1, true);
    }, [isLoading, isLoadingMore, hasMore, page, runSearch]);

    // Infinite scroll (BR-31). `.tm-table-scroll` is the only scrollable region.
    const wrapRef = useRef(null);
    useEffect(() => {
        const scrollEl = wrapRef.current;
        if (!scrollEl) return null;

        const handleScroll = () => {
            const { scrollTop, clientHeight, scrollHeight } = scrollEl;
            // Trigger a bit before hitting the exact bottom for a smoother feel.
            if (scrollHeight - (scrollTop + clientHeight) < 120) {
                handleLoadMore();
            }
        };

        scrollEl.addEventListener('scroll', handleScroll);
        return () => scrollEl.removeEventListener('scroll', handleScroll);
    }, [handleLoadMore]);

    // language options: JP display name + code, shared by 元言語 / 先言語 selects.
    const languageOptions = Object.keys(TranslationConstant.TRANSLATION_LANG_DISPLAY_JP);
    const renderLanguageMenuItems = () => [
        <MenuItem key="__all" value="">
            {tr('すべて')}
        </MenuItem>,
        ...languageOptions.map(code => (
            <MenuItem key={code} value={code}>
                {`${TranslationConstant.TRANSLATION_LANG_DISPLAY_JP[code]} (${code})`}
            </MenuItem>
        ))
    ];

    return (
        <div className="tm-wrap" ref={wrapRef}>
            <h1 className="tm-title">{tr('翻訳管理')}</h1>
            
            <div className="tm-search-row">
                <TextField
                    className="tm-search"
                    variant="outlined"
                    size="small"
                    placeholder={tr('キーワードを入力してください')}
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                />
            </div>

            {/* row 2: 翻訳元言語 / 翻訳先言語 */}
            <div className="tm-filter-grid tm-filter-grid-2">
                <div className="tm-filter-field">
                    <label>{tr('翻訳元言語')}</label>
                    <TextField
                        select
                        className="tm-select form-control form-control--wrap form-control--hg40"
                        variant="outlined"
                        SelectProps={SELECT_PROPS}
                        value={sourceLanguageFilter}
                        onChange={e => setSourceLanguageFilter(e.target.value)}
                    >
                        {renderLanguageMenuItems()}
                    </TextField>
                </div>
                <div className="tm-filter-field">
                    <label>{tr('翻訳先言語')}</label>
                    <TextField
                        select
                        className="tm-select form-control form-control--wrap form-control--hg40"
                        variant="outlined"
                        SelectProps={SELECT_PROPS}
                        value={targetLanguageFilter}
                        onChange={e => setTargetLanguageFilter(e.target.value)}
                    >
                        {renderLanguageMenuItems()}
                    </TextField>
                </div>
            </div>

            {/* row 3: 種別 / 状態 / 用語 */}
            <div className="tm-filter-grid tm-filter-grid-3">
                <div className="tm-filter-field">
                    <label>{tr('種別')}</label>
                    <TextField
                        select
                        className="tm-select form-control form-control--wrap form-control--hg40"
                        variant="outlined"
                        SelectProps={SELECT_PROPS}
                        value={usageFilter}
                        onChange={e => setUsageFilter(e.target.value)}
                    >
                        <MenuItem value="">{tr('すべて')}</MenuItem>
                        {TranslationManagementConstant.USAGE_OPTIONS.map(code => (
                            <MenuItem key={code} value={code}>
                                {tr(TranslationManagementConstant.USAGE_META[code].label)}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                <div className="tm-filter-field">
                    <label>{tr('状態')}</label>
                    <TextField
                        select
                        className="tm-select form-control form-control--wrap form-control--hg40"
                        variant="outlined"
                        SelectProps={SELECT_PROPS}
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                    >
                        <MenuItem value="">{tr('すべて')}</MenuItem>
                        {TranslationManagementConstant.STATUS_OPTIONS.map(code => (
                            <MenuItem key={code} value={code}>
                                {tr(TranslationManagementConstant.STATUS_META[code].label)}
                            </MenuItem>
                        ))}
                    </TextField>
                </div>
                <div className="tm-filter-field">
                    <label>{tr(TranslationManagementConstant.TERM_HEADER.TEXT)}</label>
                    <TextField
                        select
                        className="tm-select form-control form-control--wrap form-control--hg40"
                        variant="outlined"
                        SelectProps={SELECT_PROPS}
                        value={termFilter}
                        onChange={e => setTermFilter(e.target.value)}
                    >
                        <MenuItem value={TranslationManagementConstant.TERM_FILTER.ALL}>
                            {tr('すべて')}
                        </MenuItem>
                        <MenuItem value={TranslationManagementConstant.TERM_FILTER.ON}>
                            {tr('ONのみ')}
                        </MenuItem>
                        <MenuItem value={TranslationManagementConstant.TERM_FILTER.OFF}>
                            {tr('OFFのみ')}
                        </MenuItem>
                    </TextField>
                </div>
            </div>

            <div className="tm-result-row">
                <span className="tm-result-count">
                    {tr('検索結果')}：{total}
                    {tr('件')}
                </span>
                <MuiTooltip title={tr('新規登録')}>
                    {/* span keeps the tooltip working while the button is disabled */}
                    <span className="tm-tooltip-wrap">
                        <IconButton
                            className="tm-add-btn"
                            size="small"
                            onClick={handleStartAddNew}
                            disabled={addingNew}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </span>
                </MuiTooltip>
            </div>

            <div className="tm-table-scroll">
                <table className="tm-table">
                    <thead>
                        <tr>
                            <th style={{ width: 28 }} />
                            <th style={{ width: 110 }}>{tr('ID')}</th>
                            <th style={{ width: 100 }}>{tr('翻訳元言語')}</th>
                            <th>{tr('キーワード')}</th>
                            <th style={{ width: 120 }}>{tr('種別')}</th>
                            <th style={{ width: 110 }} className="tm-th-term">
                                {renderTermHeader()}
                            </th>
                            <th style={{ width: 60 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {addingNew && (
                            <TerminologyDraftCard
                                orgLanguages={orgLanguages}
                                languageOptions={languageOptions}
                                defaultSourceLanguage={currentLanguage}
                                submitting={submitting}
                                serverError={newRowError}
                                renderError={renderNewRowError}
                                onSubmit={handleSaveNewRow}
                                onCancel={handleCancelAddNew}
                                onPreviewTranslate={handlePreviewTranslate}
                            />
                        )}
                        {isLoading ? (
                            <tr>
                                <td colSpan={7} className="tm-loading">
                                    <CircularProgress size={20} />
                                </td>
                            </tr>
                        ) : items.length === 0 && !addingNew ? (
                            <tr>
                                <td colSpan={7} className="tm-empty">
                                    {trI18n('MSG-CM-NO-RESULT-FOUND')}
                                </td>
                            </tr>
                        ) : (
                            items.map(item => {
                                const meta = TranslationManagementConstant.USAGE_META[
                                    item.usage
                                ] || {
                                    label: item.usage,
                                    cls: 'tm-badge-ui'
                                };
                                const rowKey = rowKeyOf(item);
                                const detail = expanded[rowKey];
                                const isOpen = Boolean(detail);
                                const isTerm = item.useAsTerminology === 'Y';
                                return (
                                    <React.Fragment
                                        key={item.id || `${item.usage}-${item.sourceHash}`}
                                    >
                                        <tr className={`tm-src-row${isOpen ? ' tm-open' : ''}`}>
                                            <td>
                                                {/* Only the caret expands / collapses the row. */}
                                                <button
                                                    type="button"
                                                    className="tm-caret-btn"
                                                    aria-expanded={isOpen}
                                                    aria-label={isOpen ? tr('閉じる') : tr('開く')}
                                                    onClick={() => handleToggleRow(item)}
                                                >
                                                    <span
                                                        className={`tm-caret${isOpen ? ' tm-caret-open' : ''}`}
                                                        aria-hidden="true"
                                                    />
                                                </button>
                                            </td>
                                            <td className="tm-id">{item.entryId || '-'}</td>
                                            <td className="tm-lang">{item.sourceLanguage}</td>
                                            <TerminologyKeywordCell
                                                keyword={item.sourceText}
                                                isEditable={isTerminologyEntry(item)}
                                                submitting={submitting}
                                                onSave={value => handleSaveKeyword(item, value)}
                                                renderError={renderNewRowError}
                                            />
                                            <td>
                                                <span className={`tm-badge ${meta.cls}`}>
                                                    {tr(meta.label)}
                                                </span>
                                            </td>
                                            <td onClick={e => e.stopPropagation()}>
                                                <div className="tm-term-cell">
                                                    <TmSwitch
                                                        id={`tm-term-${item.usage}-${item.sourceHash}`}
                                                        checked={isTerm}
                                                        disabled={submitting}
                                                        onChange={e =>
                                                            handleToggleTerminology(
                                                                item,
                                                                e.target.checked
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </td>
                                            <td
                                                className="tm-row-actions"
                                                onClick={e => e.stopPropagation()}
                                            >
                                                <MuiTooltip title={tr('削除')}>
                                                    <span className="tm-tooltip-wrap">
                                                        <button
                                                            type="button"
                                                            className="tm-icon-btn tm-icon-danger"
                                                            // Every usage can be deleted; a system usage
                                                            // (UI / FIELD_LABEL / TAG) is translated again on next use.
                                                            disabled={submitting}
                                                            onClick={() => setDeletingItem(item)}
                                                        >
                                                            <img src="/assets/image/delete-icon.svg" alt="" />
                                                        </button>
                                                    </span>
                                                </MuiTooltip>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="tm-detail">
                                                <td colSpan={7}>
                                                    <TranslationRowDetail
                                                        loading={detail.loading && detail.rows.length === 0}
                                                        rows={detail.rows}
                                                        editingRowKey={
                                                            editing?.rowKey === rowKey ? editing.targetLanguage : null
                                                        }
                                                        editText={editText}
                                                        editError={editError ? renderNewRowError(editError) : ''}
                                                        onEditTextChange={handleEditTextChange}
                                                        onStartEdit={row => handleStartEditRow(item, row)}
                                                        onCancelEdit={handleCancelEditRow}
                                                        onSubmitEdit={row => handleSubmitEditRow(item, row)}
                                                        onTranslate={row => handleTranslateRow(item, row)}
                                                        submitting={submitting}
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>

                {!isLoading && hasMore && (
                    <div className="tm-load-more">
                        {isLoadingMore ? (
                            <>
                                <CircularProgress size={16} />
                                <span>{tr('読み込み中…')}</span>
                            </>
                        ) : null}
                    </div>
                )}
            </div>

            <ConfirmDialog
                isOpen={Boolean(deletingItem)}
                msgConfirm={trI18n('MSG-MLT-CONFIRM-DELETE-KEYWORD')}
                okCallBack={handleConfirmDelete}
                closeCallBack={() => setDeletingItem(null)}
            />
        </div>
    );
}
